import { canSeeContent, listEvents as listHubEvents } from "@hub";

const KEY = "dh_events";

function readEvents() {
  try {
    return JSON.parse(localStorage.getItem(KEY)) || [];
  } catch {
    return [];
  }
}

function writeEvents(events) {
  localStorage.setItem(KEY, JSON.stringify(events));
}

export function peekHubCalendarEvents(role = "agent") {
  try {
    return listHubEvents()
      .filter((item) => item.starts_on)
      .filter((item) => canSeeContent(item.visibility, role))
      .map((item) => ({
        id: `hub:${item.id}`,
        agentId: null,
        buyerId: null,
        title: item.title,
        date: item.starts_on,
        time: item.starts_at || "09:00",
        type: "hub",
        status: "scheduled",
        location: item.location || "",
        description: item.summary || "",
        contactName: "",
        notes: item.region ? `Hub · ${item.region}` : "Hub event",
        source: "hub",
        region: item.region || "San Diego County",
        readOnly: true,
        repeat: "none",
        reminders: [],
        sendEmail: false,
        sendPhoneCalendar: false,
        sendDashboardAlert: false,
      }));
  } catch {
    return [];
  }
}

export function peekEvents(agentId, excludeAgentId) {
  let events = readEvents();
  if (agentId) events = events.filter((item) => item.agentId === agentId);
  else if (excludeAgentId) events = events.filter((item) => item.agentId && item.agentId !== excludeAgentId);
  return events.sort((a, b) => `${a.date} ${a.time}`.localeCompare(`${b.date} ${b.time}`));
}

export function listEvents(agentId, excludeAgentId, options = {}) {
  const desk = peekEvents(agentId, excludeAgentId);
  if (options.includeHub === false) return Promise.resolve(desk);
  const role = options.role || (excludeAgentId ? "broker" : "agent");
  const hub = peekHubCalendarEvents(role);
  return Promise.resolve(
    [...desk, ...hub].sort((a, b) => `${a.date} ${a.time}`.localeCompare(`${b.date} ${b.time}`))
  );
}

function normalize(payload) {
  return {
    id: payload.id || crypto.randomUUID(),
    agentId: payload.agentId,
    buyerId: payload.buyerId || null,
    title: payload.title,
    date: payload.date,
    time: payload.time,
    type: payload.type || "appointment",
    status: payload.status || "scheduled",
    location: payload.location || "",
    description: payload.description || "",
    contactName: payload.contactName || "",
    notes: payload.notes || "",
    repeat: payload.repeat || "none",
    reminders: Array.isArray(payload.reminders) ? payload.reminders : [{ amount: 1, unit: "hour" }],
    sendEmail: Boolean(payload.sendEmail),
    sendPhoneCalendar: Boolean(payload.sendPhoneCalendar),
    sendDashboardAlert: payload.sendDashboardAlert !== false,
  };
}

export function createEvent(payload) {
  const events = readEvents();
  const event = normalize(payload);
  events.push(event);
  writeEvents(events);
  return Promise.resolve(event);
}

export function updateEvent(id, payload) {
  if (String(id).startsWith("hub:")) {
    return Promise.reject(new Error("Hub events are read-only on the desk calendar"));
  }
  const events = readEvents();
  const index = events.findIndex((item) => item.id === id);
  if (index === -1) return Promise.reject(new Error("Event not found"));
  events[index] = normalize({ ...events[index], ...payload, id });
  writeEvents(events);
  return Promise.resolve(events[index]);
}

export function seedEvents(events) {
  if (!readEvents().length) writeEvents(events);
}

export function ensureEvents(events) {
  const current = readEvents();
  let changed = false;
  events.forEach((item) => {
    if (!current.some((row) => row.id === item.id)) {
      current.push(item);
      changed = true;
    }
  });
  if (changed) writeEvents(current);
}
